/**
 * Which conversation this browser TAB is holding, so a second tab doesn't take it.
 *
 * The panel restores the most recently updated conversation when it opens. A
 * streaming conversation is rewritten on every token, so it is ALWAYS the most
 * recent one — meaning a second tab opened mid-answer adopted the first tab's
 * `conversationId` and `sessionId`. From there: its question was silently dropped
 * (the sidecar hands back the live run for that conversation), it displayed the
 * other tab's answer, its ■ button killed the other tab's turn, and both tabs
 * wrote their own `messages` array under the same key.
 *
 * The claim therefore has to answer a sharper question than "is this taken?" —
 * it must answer "is this taken BY SOMEONE ELSE?". A reload is the same tab
 * coming back to its own conversation, and it must be allowed to: refusing it
 * would break exactly the case the whole reconnection story exists for.
 *
 * That distinction is what `sessionStorage` gives us for free: it survives a
 * reload and is NOT shared between tabs, so it is a tab identity. The claim
 * itself lives in `localStorage`, which is shared — that is the point.
 *
 * Deliberately not a lock: it carries a timestamp and expires, so a tab that
 * crashes or is closed mid-turn never strands a conversation. A stale claim
 * simply means "nobody is there any more".
 */

const PREFIX = 'orbit.builder.chat.claim.'
const TAB_KEY = 'orbit.builder.chat.tab'

// Long enough to survive a slow render or a backgrounded tab between refreshes,
// short enough that a closed tab frees its conversation quickly.
const CLAIM_TTL_MS = 30000

const key = (conversationId) => `${PREFIX}${conversationId}`

/** This tab's identity — stable across reloads, unique per tab. */
function tabId() {
  try {
    let id = sessionStorage.getItem(TAB_KEY)
    if (!id) {
      id = globalThis.crypto?.randomUUID?.() ?? String(Math.random()).slice(2)
      sessionStorage.setItem(TAB_KEY, id)
    }
    return id
  }
  catch { return 'no-storage' }
}

function read(conversationId) {
  try {
    const raw = localStorage.getItem(key(conversationId))
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return typeof parsed?.at === 'number' ? parsed : null
  }
  catch { return null }
}

/**
 * Is another tab holding this conversation right now?
 *
 * False for our own claim — that is a reload, and this tab is entitled to pick
 * its conversation back up.
 */
export function isClaimedByAnotherTab(conversationId) {
  if (!conversationId) return false
  const held = read(conversationId)
  if (!held) return false
  if (Date.now() - held.at >= CLAIM_TTL_MS) return false
  return held.tab !== tabId()
}

/** Take (or refresh) this tab's claim. */
export function claim(conversationId) {
  if (!conversationId) return
  try { localStorage.setItem(key(conversationId), JSON.stringify({ tab: tabId(), at: Date.now() })) }
  catch { /* private mode — one tab then, which is the safe assumption */ }
}

/** Give it up: another tab may take the conversation immediately. */
export function release(conversationId) {
  if (!conversationId) return
  try { localStorage.removeItem(key(conversationId)) }
  catch { /* ignore */ }
}

export const __testing = { CLAIM_TTL_MS, PREFIX, TAB_KEY, tabId }
